import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import api from '../services/api';
import { ORGANIZATION_BRAND, corporateEmailPlaceholder } from '../config/organization';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: localStorage.getItem('rememberMe') === 'true' ? localStorage.getItem('userEmail') || '' : '',
    password: '',
    remember: localStorage.getItem('rememberMe') === 'true'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (!validateEmail(resetEmail)) {
      showError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }
    try {
      setIsResetting(true);
      const response = await api.forgotPassword(resetEmail.trim());
      showSuccess(response.message || 'Geçici şifre SMS ile gönderildi.');
      setShowForgotPassword(false);
    } catch (error) {
      showError(error.message || 'Şifre sıfırlama SMS’i gönderilemedi.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // E-posta formatı kontrolü
    if (!validateEmail(formData.email)) {
      showError('Lütfen geçerli bir e-posta adresi girin.');
      setIsLoading(false);
      return;
    }

    // Şifre kontrolü
    if (formData.password.length < 6) {
      showError('Şifre en az 6 karakter olmalıdır.');
      setIsLoading(false);
      return;
    }

    // Backend'e login isteği gönder
    try {
      const response = await api.login(formData.email, formData.password);
      setIsLoading(false);

      if (response.success) {
        // Authentication bilgilerini localStorage'a kaydet
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userEmail', formData.email);
        localStorage.setItem('userToken', response.token);
        localStorage.setItem('userData', JSON.stringify(response.user));
        localStorage.setItem('loginTime', new Date().toISOString());

        const normalizedRole = response.user?.role || 'employee';
        const defaultCompanyId = response.user?.companyId || '';
        localStorage.setItem('activeRole', normalizedRole);

        if (normalizedRole === 'super_admin') {
          if (defaultCompanyId) {
            localStorage.setItem('activeCompanyId', defaultCompanyId);
          } else {
            localStorage.removeItem('activeCompanyId');
            localStorage.removeItem('activeCompanyName');
          }
        } else if (defaultCompanyId) {
          localStorage.setItem('activeCompanyId', defaultCompanyId);
          localStorage.setItem('activeCompanyName', response.user?.company || 'Kendi firmanız');
        }
        
        // Beni hatırla seçeneği kontrolü
        if (formData.remember) {
          localStorage.setItem('rememberMe', 'true');
        }
        
        showSuccess('Giriş başarılı! Yönlendiriliyorsunuz...');
        setTimeout(() => {
          navigate('/anasayfa');
        }, 1500);
      } else {
        showError(response.message || 'Giriş başarısız.');
      }
    } catch (error) {
      setIsLoading(false);
      showError('Sunucu hatası. Lütfen tekrar deneyin.');
      console.error('Login error:', error);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-page">
      {/* Animated Background */}
      <div className="login-background">
        <div className="floating-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
          <div className="shape shape-4"></div>
          <div className="shape shape-5"></div>
          <div className="shape shape-6"></div>
        </div>
        <div className="gradient-overlay"></div>
      </div>

      <div className="login-form-wrapper">
        <div className="login-form-container">
          <div className="form-header">
            <div className="logo-mobile">
              <img
                src={ORGANIZATION_BRAND.logoPath}
                alt={`${ORGANIZATION_BRAND.name} Logo`}
                className="img-fluid brand-logo-light"
              />
              <img
                src={ORGANIZATION_BRAND.logoDarkPath}
                alt={`${ORGANIZATION_BRAND.name} Logo`}
                className="img-fluid brand-logo-dark"
              />
            </div>
            <h2 className="form-title">Kurumsal Giriş</h2>
            <p className="form-subtitle">Şirket e-posta adresinizle güvenli giriş yapın</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {/* E-posta Alanı */}
            <div className="form-group mb-4">
              <label htmlFor="email" className="form-label">
                <i className="bx bx-envelope me-2"></i>
                E-posta Adresi
              </label>
              <div className="input-wrapper">
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  placeholder={corporateEmailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <div className="input-focus-line"></div>
              </div>
            </div>

            <p className="corporate-domain-hint">Kayıtlı herhangi bir e-posta adresinizle giriş yapabilirsiniz.</p>

            {/* Şifre Alanı */}
            <div className="form-group mb-4">
              <label htmlFor="password" className="form-label">
                <i className="bx bx-lock me-2"></i>
                Şifre
              </label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  id="password"
                  name="password"
                  placeholder="Şifrenizi girin"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                >
                  <i className={`bx ${showPassword ? 'bx-show' : 'bx-hide'}`}></i>
                </button>
                <div className="input-focus-line"></div>
              </div>
            </div>

            {/* Beni Hatırla ve Şifremi Unuttum */}
            <div className="form-options mb-4">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="remember"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleInputChange}
                />
                <label className="form-check-label" htmlFor="remember">
                  Beni hatırla
                </label>
              </div>
              <button
                type="button"
                className="forgot-password"
                onClick={() => {
                  setResetEmail(formData.email);
                  setShowForgotPassword((current) => !current);
                }}
              >
                Şifremi unuttum?
              </button>
            </div>

            {showForgotPassword && (
              <div className="mb-4">
                <label htmlFor="resetEmail" className="form-label">Şifrenin gönderileceği e-posta</label>
                <input
                  type="email"
                  className="form-control mb-2"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event.target.value)}
                  placeholder={corporateEmailPlaceholder}
                />
                <button
                  type="button"
                  className="btn-login"
                  onClick={handleForgotPassword}
                  disabled={isResetting}
                >
                  {isResetting ? 'SMS gönderiliyor…' : 'Geçici şifreyi SMS ile gönder'}
                </button>
              </div>
            )}

            {/* Giriş Butonu */}
            <button
              type="submit"
              className="btn-login"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Giriş yapılıyor...
                </>
              ) : (
                <>
                  <i className="bx bx-log-in me-2"></i>
                  Giriş Yap
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default LoginPage;
